import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import TablaIcon from '../components/TablaIcon';
import t from '../theme';

const emptyForm = { title:'', taal_id:'', type_id:'', notation:'', student_ids:[], class_ids:[] };

// ── View mode icons ────────────────────────────────────────────────────────
const GridIcon  = () => <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="0" y="0" width="6" height="6" rx="1"/><rect x="8" y="0" width="6" height="6" rx="1"/><rect x="0" y="8" width="6" height="6" rx="1"/><rect x="8" y="8" width="6" height="6" rx="1"/></svg>;
const ListIcon  = () => <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="0" y="1" width="14" height="2" rx="1"/><rect x="0" y="6" width="14" height="2" rx="1"/><rect x="0" y="11" width="14" height="2" rx="1"/></svg>;
const TableIcon = () => <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="0" y="0" width="14" height="3" rx="1"/><rect x="0" y="4" width="6" height="2" rx="1"/><rect x="8" y="4" width="6" height="2" rx="1"/><rect x="0" y="7" width="6" height="2" rx="1"/><rect x="8" y="7" width="6" height="2" rx="1"/><rect x="0" y="10" width="6" height="2" rx="1"/><rect x="8" y="10" width="6" height="2" rx="1"/></svg>;

export default function LessonsPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [lessons,  setLessons]  = useState([]);
  const [students, setStudents] = useState([]);
  const [classes,  setClasses]  = useState([]);
  const [taals,    setTaals]    = useState([]);
  const [types,    setTypes]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [filterTaal,  setFilterTaal]  = useState('');
  const [filterType,  setFilterType]  = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterMedia, setFilterMedia] = useState('');
  const [sortBy,      setSortBy]      = useState('newest');
  const [showFilters, setShowFilters] = useState(false);

  // group: 'all' | 'taal' | 'type'
  // display: 'grid' | 'list' | 'table'
  const [group,   setGroup]   = useState('all');
  const [display, setDisplay] = useState(() => localStorage.getItem('lessonsDisplay') || 'grid');

  const [modalOpen,    setModalOpen]    = useState(false);
  const [editLesson,   setEditLesson]   = useState(null);
  const [form,         setForm]         = useState(emptyForm);
  const [formError,    setFormError]    = useState('');
  const [saving,       setSaving]       = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [lr, tr, tyr] = await Promise.all([
        axios.get('/api/lessons'),
        axios.get('/api/taals'),
        axios.get('/api/types'),
      ]);
      setLessons(lr.data); setTaals(tr.data); setTypes(tyr.data);
      if (isAdmin) {
        const [sr, cr] = await Promise.all([axios.get('/api/students'), axios.get('/api/classes')]);
        setStudents(sr.data); setClasses(cr.data);
        console.log('classes loaded:', cr.data.length);
      }
    } catch(e) { console.error(e); } finally { setLoading(false); }
  }, [isAdmin]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const setDisplayMode = m => { setDisplay(m); localStorage.setItem('lessonsDisplay', m); };

  const openAdd = () => { setEditLesson(null); setForm(emptyForm); setFormError(''); setModalOpen(true); };
  const openEdit = lesson => {
    setEditLesson(lesson);
    setForm({ title:lesson.title, taal_id:String(lesson.taal_id), type_id:String(lesson.type_id),
      notation:lesson.notation||'', student_ids:lesson.assignedStudents?.map(s=>s.id)||[],
      class_ids:lesson.classes?.map(c=>c.id)||[] });
    setFormError(''); setModalOpen(true);
  };
  const toggleClass = id => setForm(f => ({
    ...f, class_ids: f.class_ids.includes(id) ? f.class_ids.filter(x=>x!==id) : [...f.class_ids, id],
  }));
  const toggleStudent = id => setForm(f => ({
    ...f, student_ids: f.student_ids.includes(id) ? f.student_ids.filter(x=>x!==id) : [...f.student_ids, id],
  }));

  const handleSave = async () => {
    setFormError('');
    if (!form.title.trim()) return setFormError('Title is required.');
    if (!form.taal_id)      return setFormError('Please select a Taal.');
    if (!form.type_id)      return setFormError('Please select a Type.');
    setSaving(true);
    try {
      if (editLesson) await axios.put(`/api/lessons/${editLesson.id}`, form);
      else            await axios.post('/api/lessons', form);
      setModalOpen(false); fetchAll();
    } catch(e) { setFormError(e.response?.data?.error || 'Failed to save.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await axios.delete(`/api/lessons/${deleteTarget.id}`); setDeleteTarget(null); fetchAll(); }
    catch(e) { console.error(e); }
  };

  // ── Filter lessons ────────────────────────────────────────────────────────
  const filtered = lessons.filter(l => {
    if (search && !l.title.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterTaal && String(l.taal_id) !== filterTaal) return false;
    if (filterType && String(l.type_id) !== filterType) return false;
    if (filterClass === 'standalone' && l.classes?.length > 0) return false;
    if (filterClass && filterClass !== 'standalone' && !l.classes?.some(c => String(c.id) === filterClass)) return false;
    if (filterMedia === 'yes' && !l.video_url) return false;
    if (filterMedia === 'no' && l.video_url) return false;
    return true;
  }).sort((a, b) => {
    if (sortBy === 'newest') return new Date(b.createdAt) - new Date(a.createdAt);
    if (sortBy === 'oldest') return new Date(a.createdAt) - new Date(b.createdAt);
    if (sortBy === 'az') return a.title.localeCompare(b.title);
    if (sortBy === 'za') return b.title.localeCompare(a.title);
    return 0;
  });

  const activeFilters = [
    filterTaal  && { key:'taal',  label: `Taal: ${taals.find(t=>String(t.id)===filterTaal)?.name||''}`,  clear: ()=>setFilterTaal('') },
    filterType  && { key:'type',  label: `Type: ${types.find(t=>String(t.id)===filterType)?.name||''}`,  clear: ()=>setFilterType('') },
    filterClass === 'standalone' && { key:'class', label: 'Standalone only', clear: ()=>setFilterClass('') },
    filterClass && filterClass !== 'standalone' && { key:'class', label: `Class: ${classes.find(c=>String(c.id)===filterClass)?.title||''}`, clear: ()=>setFilterClass('') },
    filterMedia === 'yes' && { key:'media', label: 'Has media', clear: ()=>setFilterMedia('') },
    filterMedia === 'no'  && { key:'media', label: 'No media',  clear: ()=>setFilterMedia('') },
    sortBy !== 'newest'   && { key:'sort',  label: `Sort: ${sortBy==='oldest'?'Oldest':sortBy==='az'?'A-Z':'Z-A'}`, clear: ()=>setSortBy('newest') },
  ].filter(Boolean);

  const groupByTaal = () => {
    const map = {};
    taals.forEach(tl => {
      const g = filtered.filter(l => l.taal_id === tl.id);
      if (g.length) map[tl.id] = { label: tl.name, lessons: g };
    });
    return map;
  };

  const groupByType = () => {
    const map = {};
    types.forEach(tp => {
      const g = filtered.filter(l => l.type_id === tp.id);
      if (g.length) map[tp.id] = { label: tp.name, lessons: g };
    });
    return map;
  };

  // ── Grid Card ─────────────────────────────────────────────────────────────
  const GridCard = ({ lesson }) => (
    <div style={{ ...t.card, padding:'16px 18px', display:'flex', flexDirection:'column', gap:'9px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <h3 style={{ fontSize:'14px', fontWeight:500, color:t.text, margin:0, flex:1, marginRight:'8px' }}>{lesson.title}</h3>
        {isAdmin && (
          <div style={{ display:'flex', gap:'5px', flexShrink:0 }}>
            <button style={{ ...t.btnSecondary, padding:'3px 8px', fontSize:'11px' }} onClick={() => openEdit(lesson)}>Edit</button>
            <button style={{ ...t.btnDanger, padding:'3px 8px', fontSize:'11px' }} onClick={() => setDeleteTarget(lesson)}>Del</button>
          </div>
        )}
      </div>
      <div style={{ display:'flex', gap:'5px', flexWrap:'wrap' }}>
        <span style={{ background:t.accentLight, border:`1px solid ${t.accentBorder}`, color:t.accent,
          padding:'2px 9px', borderRadius:'20px', fontSize:'11px', fontWeight:500,
          display:'flex', alignItems:'center', gap:'4px' }}>
          <TablaIcon size={10} color={t.accent}/> {lesson.taal?.name}
        </span>
        <span style={{ background:t.surface2, border:`1px solid ${t.border}`, color:t.text3,
          padding:'2px 9px', borderRadius:'20px', fontSize:'11px' }}>{lesson.lessonType?.name}</span>
        {lesson.video_url && <span style={{ background:t.greenLight, color:t.green, padding:'2px 9px', borderRadius:'20px', fontSize:'11px' }}>▷ Media</span>}
        {lesson.comments?.length > 0 && <span style={{ background:t.surface2, color:t.text3, padding:'2px 9px', borderRadius:'20px', fontSize:'11px' }}>💬 {lesson.comments.length}</span>}
      </div>
      {lesson.notation && (
        <p style={{ fontSize:'12px', color:t.text3, margin:0, lineHeight:1.5,
          overflow:'hidden', textOverflow:'ellipsis', display:'-webkit-box',
          WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>{lesson.notation}</p>
      )}
      {isAdmin && lesson.assignedStudents?.length > 0 && (
        <div style={{ display:'flex', flexWrap:'wrap', gap:'4px', paddingTop:'4px', borderTop:`1px solid ${t.border}` }}>
          {lesson.assignedStudents.slice(0,3).map(s => (
            <span key={s.id} style={{ background:t.surface2, color:t.text3, padding:'2px 8px', borderRadius:'20px', fontSize:'11px' }}>{s.full_name}</span>
          ))}
          {lesson.assignedStudents.length > 3 && <span style={{ color:t.text3, fontSize:'11px', padding:'2px 4px' }}>+{lesson.assignedStudents.length-3}</span>}
        </div>
      )}
      <button style={{ ...t.btnSecondary, textAlign:'center', marginTop:'auto', fontSize:'12px' }}
        onClick={() => navigate(`/lessons/${lesson.id}`)}>Open →</button>
    </div>
  );

  // ── List Row ──────────────────────────────────────────────────────────────
  const ListRow = ({ lesson }) => (
    <div style={{ ...t.card, padding:'12px 16px', display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap' }}>
      <div style={{ flex:1, minWidth:'160px' }}>
        <div style={{ fontSize:'14px', fontWeight:500, color:t.text, marginBottom:'4px' }}>{lesson.title}</div>
        <div style={{ display:'flex', gap:'5px', flexWrap:'wrap', alignItems:'center' }}>
          <span style={{ background:t.accentLight, border:`1px solid ${t.accentBorder}`, color:t.accent,
            padding:'1px 8px', borderRadius:'20px', fontSize:'11px' }}>{lesson.taal?.name}</span>
          <span style={{ background:t.surface2, border:`1px solid ${t.border}`, color:t.text3,
            padding:'1px 8px', borderRadius:'20px', fontSize:'11px' }}>{lesson.lessonType?.name}</span>
          {lesson.video_url && <span style={{ color:t.green, fontSize:'11px' }}>▷ Media</span>}
          {lesson.comments?.length > 0 && <span style={{ color:t.text3, fontSize:'11px' }}>💬 {lesson.comments.length}</span>}
        </div>
      </div>
      {isAdmin && lesson.assignedStudents?.length > 0 && (
        <div style={{ display:'flex', flexWrap:'wrap', gap:'4px', flexShrink:0 }}>
          {lesson.assignedStudents.slice(0,2).map(s => (
            <span key={s.id} style={{ background:t.surface2, color:t.text3, padding:'2px 8px', borderRadius:'20px', fontSize:'11px' }}>{s.full_name}</span>
          ))}
          {lesson.assignedStudents.length > 2 && <span style={{ color:t.text3, fontSize:'11px' }}>+{lesson.assignedStudents.length-2}</span>}
        </div>
      )}
      <div style={{ display:'flex', gap:'5px', flexShrink:0 }}>
        <button style={{ ...t.btnSecondary, padding:'5px 12px', fontSize:'12px' }}
          onClick={() => navigate(`/lessons/${lesson.id}`)}>Open →</button>
        {isAdmin && <>
          <button style={{ ...t.btnSecondary, padding:'5px 10px', fontSize:'12px' }} onClick={() => openEdit(lesson)}>Edit</button>
          <button style={{ ...t.btnDanger, padding:'5px 10px', fontSize:'12px' }} onClick={() => setDeleteTarget(lesson)}>Del</button>
        </>}
      </div>
    </div>
  );

  // ── Table View ────────────────────────────────────────────────────────────
  const TableView = ({ items }) => (
    <div style={{ ...t.card, overflow:'hidden' }}>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead>
          <tr>
            {['Title','Taal','Type','Media','Comments', isAdmin?'Students':'', 'Actions'].filter(Boolean).map(h => (
              <th key={h} style={{ padding:'10px 14px', fontSize:'11px', fontWeight:600, color:t.text3,
                textTransform:'uppercase', letterSpacing:'0.6px', textAlign:'left',
                borderBottom:`1px solid ${t.border}`, background:t.surface2 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map(lesson => (
            <tr key={lesson.id} style={{ borderBottom:`1px solid ${t.border}` }}
              onMouseEnter={e => e.currentTarget.style.background=t.surface2}
              onMouseLeave={e => e.currentTarget.style.background=''}>
              <td style={{ padding:'10px 14px', fontSize:'13px', color:t.text, fontWeight:500 }}>{lesson.title}</td>
              <td style={{ padding:'10px 14px' }}>
                <span style={{ background:t.accentLight, color:t.accent, padding:'2px 8px', borderRadius:'20px', fontSize:'11px' }}>{lesson.taal?.name}</span>
              </td>
              <td style={{ padding:'10px 14px' }}>
                <span style={{ background:t.surface2, color:t.text3, padding:'2px 8px', borderRadius:'20px', fontSize:'11px' }}>{lesson.lessonType?.name}</span>
              </td>
              <td style={{ padding:'10px 14px', fontSize:'12px', color: lesson.video_url ? t.green : t.text3 }}>
                {lesson.video_url ? '▷ Yes' : '—'}
              </td>
              <td style={{ padding:'10px 14px', fontSize:'12px', color:t.text3 }}>
                {lesson.comments?.length || 0}
              </td>
              {isAdmin && (
                <td style={{ padding:'10px 14px', fontSize:'12px', color:t.text3 }}>
                  {lesson.assignedStudents?.map(s=>s.full_name).join(', ')||'—'}
                </td>
              )}
              <td style={{ padding:'10px 14px' }}>
                <div style={{ display:'flex', gap:'5px' }}>
                  <button style={{ ...t.btnSecondary, padding:'4px 10px', fontSize:'11px' }}
                    onClick={() => navigate(`/lessons/${lesson.id}`)}>Open</button>
                  {isAdmin && <>
                    <button style={{ ...t.btnSecondary, padding:'4px 8px', fontSize:'11px' }} onClick={() => openEdit(lesson)}>Edit</button>
                    <button style={{ ...t.btnDanger, padding:'4px 8px', fontSize:'11px' }} onClick={() => setDeleteTarget(lesson)}>Del</button>
                  </>}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // ── Render lessons in current display mode ────────────────────────────────
  const RenderLessons = ({ items }) => {
    if (display === 'grid')  return <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:'12px' }}>{items.map(l => <GridCard key={l.id} lesson={l}/>)}</div>;
    if (display === 'list')  return <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>{items.map(l => <ListRow key={l.id} lesson={l}/>)}</div>;
    if (display === 'table') return <TableView items={items}/>;
    return null;
  };

  const GroupSection = ({ label, items, icon }) => (
    <div style={{ marginBottom:'24px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'12px' }}>
        {icon}
        <span style={{ fontSize:'13px', fontWeight:600, color:t.text }}>{label}</span>
        <span style={{ fontSize:'11px', color:t.text3 }}>· {items.length}</span>
      </div>
      <RenderLessons items={items}/>
    </div>
  );

  const displayModes = [
    { key:'grid',  label:'Grid',  Icon: GridIcon },
    { key:'list',  label:'List',  Icon: ListIcon },
    { key:'table', label:'Table', Icon: TableIcon },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'16px', flexWrap:'wrap', gap:'12px' }}>
        <div>
          <h2 style={{ fontSize:'22px', fontWeight:600, color:t.text, letterSpacing:'-0.3px', margin:0 }}>
            {isAdmin ? 'Lessons' : 'My Lessons'}
          </h2>
          <p style={{ fontSize:'13px', color:t.text3, margin:'4px 0 0' }}>{filtered.length} lesson{filtered.length!==1?'s':''}</p>
        </div>
        <div style={{ display:'flex', gap:'8px', alignItems:'center', flexWrap:'wrap' }}>

          {/* Group toggle */}
          <div style={{ display:'flex', background:t.surface2, borderRadius:'8px', padding:'3px', gap:'2px', border:`1px solid ${t.border}` }}>
            {(['all','taal','type']).map(v => (
              <button key={v} onClick={() => setGroup(v)}
                style={{ padding:'5px 12px', borderRadius:'6px', border:'none', fontFamily:t.font,
                  fontSize:'12px', fontWeight: group===v ? 600 : 400, cursor:'pointer',
                  background: group===v ? t.accent : 'none',
                  color: group===v ? '#0e0e0d' : t.text3 }}>
                {v === 'all' ? 'All' : v === 'taal' ? 'By Taal' : 'By Type'}
              </button>
            ))}
          </div>

          {/* Display mode toggle */}
          <div style={{ display:'flex', background:t.surface2, borderRadius:'8px', padding:'3px', gap:'2px', border:`1px solid ${t.border}` }}>
            {displayModes.map(({ key, label, Icon }) => (
              <button key={key} onClick={() => setDisplayMode(key)} title={label}
                style={{ padding:'6px 10px', borderRadius:'6px', border:'none', cursor:'pointer',
                  background: display===key ? t.accent : 'none',
                  color: display===key ? '#0e0e0d' : t.text3,
                  display:'flex', alignItems:'center', justifyContent:'center' }}>
                <Icon/>
              </button>
            ))}
          </div>

          {isAdmin && <button style={t.btnPrimary} onClick={openAdd}>+ New Lesson</button>}
        </div>
      </div>

      {/* Search + Filter Bar */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', alignItems:'center' }}>
          <input style={{ ...t.input, flex:1, minWidth:'160px', maxWidth:'280px' }}
            placeholder="🔍 Search lessons..." value={search}
            onChange={e => setSearch(e.target.value)}/>
          <button onClick={() => setShowFilters(f => !f)}
            style={{ ...t.btnSecondary, padding:'8px 14px', fontSize:'12px', display:'flex', alignItems:'center', gap:'6px',
              border: (activeFilters.length > 0) ? `1px solid ${t.accent}` : undefined,
              color: (activeFilters.length > 0) ? t.accent : undefined }}>
            ⚙ Filters {activeFilters.length > 0 && <span style={{ background:t.accent, color:'#0e0e0d', borderRadius:'20px', padding:'0px 6px', fontSize:'10px', fontWeight:700 }}>{activeFilters.length}</span>}
          </button>
          {(search || activeFilters.length > 0) && (
            <button style={{ ...t.btnSecondary, padding:'8px 14px', fontSize:'12px' }}
              onClick={() => { setSearch(''); setFilterTaal(''); setFilterType(''); setFilterClass(''); setFilterMedia(''); setSortBy('newest'); }}>
              Clear All
            </button>
          )}
        </div>

        {/* Expandable filter panel */}
        {showFilters && (
          <div style={{ background:t.surface2, border:`1px solid ${t.border}`, borderRadius:t.r,
            padding:'14px', marginTop:'10px', display:'grid',
            gridTemplateColumns:'repeat(auto-fill, minmax(min(180px,100%), 1fr))', gap:'10px' }}>
            <div>
              <div style={{ fontSize:'11px', color:t.text3, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'5px' }}>Taal</div>
              <select style={{ ...t.select, width:'100%' }} value={filterTaal} onChange={e => setFilterTaal(e.target.value)}>
                <option value="">All Taals</option>
                {taals.map(tl => <option key={tl.id} value={String(tl.id)}>{tl.name}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize:'11px', color:t.text3, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'5px' }}>Type</div>
              <select style={{ ...t.select, width:'100%' }} value={filterType} onChange={e => setFilterType(e.target.value)}>
                <option value="">All Types</option>
                {types.map(tp => <option key={tp.id} value={String(tp.id)}>{tp.name}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize:'11px', color:t.text3, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'5px' }}>Class</div>
              <select style={{ ...t.select, width:'100%' }} value={filterClass} onChange={e => setFilterClass(e.target.value)}>
                <option value="">All Lessons</option>
                <option value="standalone">Standalone Only</option>
                {classes.map(c => <option key={c.id} value={String(c.id)}>{c.title}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize:'11px', color:t.text3, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'5px' }}>Media</div>
              <select style={{ ...t.select, width:'100%' }} value={filterMedia} onChange={e => setFilterMedia(e.target.value)}>
                <option value="">Any</option>
                <option value="yes">Has Media</option>
                <option value="no">No Media</option>
              </select>
            </div>
            <div>
              <div style={{ fontSize:'11px', color:t.text3, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'5px' }}>Sort By</div>
              <select style={{ ...t.select, width:'100%' }} value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="az">Title A → Z</option>
                <option value="za">Title Z → A</option>
              </select>
            </div>
          </div>
        )}

        {/* Active filter chips */}
        {activeFilters.length > 0 && (
          <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', marginTop:'8px' }}>
            {activeFilters.map(f => (
              <span key={f.key} onClick={f.clear}
                style={{ background:t.accentLight, border:`1px solid ${t.accentBorder}`, color:t.accent,
                  padding:'3px 10px', borderRadius:'20px', fontSize:'11px', cursor:'pointer',
                  display:'flex', alignItems:'center', gap:'5px' }}>
                {f.label} <span style={{ fontWeight:700 }}>✕</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ textAlign:'center', color:t.text3, padding:'48px' }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{ ...t.card, padding:'48px', textAlign:'center' }}>
          <div style={{ display:'flex', justifyContent:'center', marginBottom:'16px', opacity:0.3 }}>
            <TablaIcon size={48} color={t.accent}/>
          </div>
          <p style={{ fontSize:'16px', fontWeight:500, color:t.text, marginBottom:'6px' }}>
            {activeFilters.length > 0 || search ? 'No lessons match your filters' : isAdmin ? 'No lessons yet' : 'No lessons assigned to you yet'}
          </p>
          {isAdmin && !search && activeFilters.length === 0 && (
            <button style={t.btnPrimary} onClick={openAdd}>+ Create First Lesson</button>
          )}
        </div>
      ) : group === 'all' ? (
        <RenderLessons items={filtered}/>
      ) : group === 'taal' ? (
        <div>
          {Object.values(groupByTaal()).map(({ label, lessons: items }) => (
            <GroupSection key={label} label={label} items={items}
              icon={<TablaIcon size={16} color={t.accent}/>}/>
          ))}
        </div>
      ) : (
        <div>
          {Object.values(groupByType()).map(({ label, lessons: items }) => (
            <GroupSection key={label} label={label} items={items}
              icon={<span style={{ fontSize:'14px', color:t.accent, fontFamily:'monospace' }}>◈</span>}/>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {modalOpen && (
        <div style={t.overlay} onClick={e => e.target===e.currentTarget && setModalOpen(false)}>
          <div style={t.modal}>
            <div style={t.modalHeader}>
              <h3 style={t.modalTitle}>{editLesson ? 'Edit Lesson' : 'New Lesson'}</h3>
              <button style={t.closeBtn} onClick={() => setModalOpen(false)}>✕</button>
            </div>
            <div style={t.modalBody}>
              <div style={t.fieldGroup}>
                <label style={t.label}>Title *</label>
                <input style={t.input} value={form.title}
                  onChange={e => setForm(f=>({...f,title:e.target.value}))} placeholder="e.g. Teental Kaida #1"/>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                <div style={t.fieldGroup}>
                  <label style={t.label}>Taal *</label>
                  <select style={t.select} value={form.taal_id}
                    onChange={e => setForm(f=>({...f,taal_id:e.target.value}))}>
                    <option value="">— Select Taal —</option>
                    {taals.map(tl => <option key={tl.id} value={String(tl.id)}>{tl.name}</option>)}
                  </select>
                </div>
                <div style={t.fieldGroup}>
                  <label style={t.label}>Type *</label>
                  <select style={t.select} value={form.type_id}
                    onChange={e => setForm(f=>({...f,type_id:e.target.value}))}>
                    <option value="">— Select Type —</option>
                    {types.map(tp => <option key={tp.id} value={String(tp.id)}>{tp.name}</option>)}
                  </select>
                </div>
              </div>
              <div style={t.fieldGroup}>
                <label style={t.label}>Notation / Notes (optional)</label>
                <textarea style={t.textarea} rows={3} value={form.notation}
                  onChange={e => setForm(f=>({...f,notation:e.target.value}))} placeholder="Notation, instructions..."/>
              </div>
              <div style={t.fieldGroup}>
                <label style={t.label}>
                  Assign to Students
                  {form.student_ids.length > 0 && (
                    <span style={{ background:t.accent, color:'#0e0e0d', borderRadius:'20px', padding:'1px 7px', fontSize:'10px', fontWeight:700, marginLeft:'6px' }}>
                      {form.student_ids.length}
                    </span>
                  )}
                </label>
                {students.length === 0 ? (
                  <p style={{ fontSize:'12px', color:t.text3, margin:0, fontStyle:'italic' }}>No students yet.</p>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:'5px', maxHeight:'200px', overflowY:'auto' }}>
                    {students.map(st => {
                      const on = form.student_ids.includes(st.id);
                      return (
                        <div key={st.id} onClick={() => toggleStudent(st.id)}
                          style={{ display:'flex', alignItems:'center', gap:'10px', padding:'9px 12px',
                            borderRadius:'8px', border:`1px solid ${on ? t.accent : t.border}`,
                            background: on ? t.accentLight : t.surface2, cursor:'pointer' }}>
                          <div style={{ width:'28px', height:'28px', borderRadius:'6px', background:t.accentLight,
                            border:`1px solid ${t.accentBorder}`, display:'flex', alignItems:'center',
                            justifyContent:'center', fontSize:'11px', fontWeight:600, color:t.accent }}>
                            {st.full_name[0]}
                          </div>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:'13px', fontWeight:500, color:t.text }}>{st.full_name}</div>
                            <div style={{ fontSize:'11px', color:t.text3 }}>@{st.user?.username}</div>
                          </div>
                          <span style={{ color:t.accent, fontWeight:700, opacity: on ? 1 : 0, fontSize:'14px' }}>✓</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {isAdmin && classes.length > 0 && (
                <div style={t.fieldGroup}>
                  <label style={t.label}>
                    Assign to Classes
                    {form.class_ids.length > 0 && <span style={{ background:t.accent, color:'#0e0e0d', borderRadius:'20px', padding:'1px 7px', fontSize:'10px', fontWeight:700, marginLeft:'6px' }}>{form.class_ids.length}</span>}
                  </label>
                  <div style={{ display:'flex', flexDirection:'column', gap:'5px', maxHeight:'150px', overflowY:'auto' }}>
                    {classes.map(c => {
                      const on = form.class_ids.includes(c.id);
                      return (
                        <div key={c.id} onClick={() => toggleClass(c.id)}
                          style={{ display:'flex', alignItems:'center', gap:'10px', padding:'9px 12px',
                            borderRadius:'8px', cursor:'pointer',
                            border:`1px solid ${on ? t.accent : t.border}`,
                            background: on ? t.accentLight : t.surface2 }}>
                          <span style={{ fontSize:'13px', fontWeight: on?500:400, color: on?t.text:t.text2, flex:1 }}>{c.title}</span>
                          {on && <span style={{ color:t.accent, fontWeight:700 }}>✓</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {formError && <div style={t.errorBox}>{formError}</div>}
            </div>
            <div style={t.modalFooter}>
              <button style={t.btnSecondary} onClick={() => setModalOpen(false)}>Cancel</button>
              <button style={t.btnPrimary} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : editLesson ? 'Save Changes' : 'Create Lesson'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <div style={t.overlay} onClick={e => e.target===e.currentTarget && setDeleteTarget(null)}>
          <div style={{ ...t.modal, maxWidth:'400px' }}>
            <div style={t.modalHeader}>
              <h3 style={t.modalTitle}>Delete Lesson</h3>
              <button style={t.closeBtn} onClick={() => setDeleteTarget(null)}>✕</button>
            </div>
            <div style={{ ...t.modalBody, textAlign:'center' }}>
              <p style={{ color:t.text2 }}>Delete <strong style={{ color:t.text }}>{deleteTarget.title}</strong>?</p>
              {deleteTarget.classes?.length > 0 && (
                <div style={{ background:t.redLight, border:`1px solid ${t.red}`, borderRadius:t.r, padding:'10px 14px', marginTop:'10px', fontSize:'13px', color:t.red }}>
                  ⚠ This lesson is assigned to {deleteTarget.classes.length} class{deleteTarget.classes.length!==1?'es':''}: {deleteTarget.classes.map(c=>c.title).join(', ')}. Deleting it will remove it from those classes too.
                </div>
              )}
            </div>
            <div style={t.modalFooter}>
              <button style={t.btnSecondary} onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button style={t.btnDanger} onClick={handleDelete}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
